'use client';

import React, { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Plus, Trash2, User } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import { type Client, type Employee } from '@/lib/data';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: Client) => void;
  initial?: Client | null;
}

type FormValues = Omit<Client, 'id' | 'createdAt'> & { id?: string; createdAt?: string };

export default function ClientForm({ open, onClose, onSave, initial }: Props) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      name: '', pib: '', address: '', city: '',
      responsiblePerson: '', phone: '', email: '',
      notes: '', employees: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'employees' });

  useEffect(() => {
    if (initial) {
      reset({
        name: initial.name, pib: initial.pib, address: initial.address,
        city: initial.city, responsiblePerson: initial.responsiblePerson,
        phone: initial.phone, email: initial.email,
        notes: initial.notes, employees: initial.employees,
        id: initial.id, createdAt: initial.createdAt,
      });
    } else {
      reset({ name: '', pib: '', address: '', city: '', responsiblePerson: '', phone: '', email: '', notes: '', employees: [] });
    }
  }, [initial, reset, open]);

  function onSubmit(data: FormValues) {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    onSave({
      ...data,
      id: data.id || `client-${Date.now()}`,
      createdAt: data.createdAt || today,
      employees: data.employees.map((e, i) => ({
        ...e,
        id: (e as Employee).id || `emp-new-${i}`,
      })),
    } as Client);
  }

  const InputError = ({ msg }: { msg?: string }) =>
    msg ? <p className="error-text">{msg}</p> : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? `Uredi: ${initial.name}` : 'Dodaj novog klijenta'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
        {/* Basic info */}
        <section>
          <h3
            className="text-sm font-semibold uppercase tracking-wide mb-4"
            style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.07em' }}
          >
            Osnovi podaci o firmi
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">Naziv firme <span style={{ color: 'hsl(var(--destructive))' }}>*</span></label>
              <input
                className="input-field"
                placeholder="npr. Alfa Commerce d.o.o."
                {...register('name', { required: 'Naziv je obavezan' })}
              />
              <InputError msg={errors.name?.message} />
            </div>
            <div>
              <label className="label">PIB <span style={{ color: 'hsl(var(--destructive))' }}>*</span></label>
              <input
                className="input-field mono"
                placeholder="9 cifara"
                {...register('pib', {
                  required: 'PIB je obavezan',
                  pattern: { value: /^\d{9}$/, message: 'PIB mora imati tačno 9 cifara' },
                })}
              />
              <p className="helper-text">Poreski identifikacioni broj (9 cifara)</p>
              <InputError msg={errors.pib?.message} />
            </div>
            <div>
              <label className="label">Odgovorno lice <span style={{ color: 'hsl(var(--destructive))' }}>*</span></label>
              <input
                className="input-field"
                placeholder="Ime i prezime"
                {...register('responsiblePerson', { required: 'Odgovorno lice je obavezno' })}
              />
              <InputError msg={errors.responsiblePerson?.message} />
            </div>
            <div>
              <label className="label">Adresa</label>
              <input className="input-field" placeholder="Ulica i broj" {...register('address')} />
            </div>
            <div>
              <label className="label">Grad</label>
              <input className="input-field" placeholder="npr. Novi Sad" {...register('city')} />
            </div>
            <div>
              <label className="label">Telefon</label>
              <input className="input-field" placeholder="+381 21 ..." {...register('phone')} />
            </div>
            <div>
              <label className="label">E-mail</label>
              <input
                type="email"
                className="input-field"
                placeholder="kontakt@firma.rs"
                {...register('email', {
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Neispravan format e-maila' },
                })}
              />
              <InputError msg={errors.email?.message} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Napomene</label>
              <textarea
                className="input-field resize-none"
                rows={3}
                placeholder="Interne napomene o klijentu..."
                {...register('notes')}
              />
            </div>
          </div>
        </section>

        {/* Divider */}
        <hr style={{ borderColor: 'hsl(var(--border))' }} />

        {/* Employees */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h3
              className="text-sm font-semibold uppercase tracking-wide"
              style={{ color: 'hsl(var(--muted-foreground))', letterSpacing: '0.07em' }}
            >
              Zaposleni i kontakti
            </h3>
            <button
              type="button"
              className="btn-secondary text-xs py-1.5 px-3"
              onClick={() => append({ id: '', name: '', position: '', phone: '', email: '' })}
            >
              <Plus size={13} />
              Dodaj zaposlenog
            </button>
          </div>
          <div className="space-y-4">
            {fields.length === 0 && (
              <div
                className="text-center py-6 rounded-xl border-2 border-dashed"
                style={{ borderColor: 'hsl(var(--border))' }}
              >
                <User size={24} className="mx-auto mb-1" style={{ color: 'hsl(var(--muted-foreground))' }} />
                <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  Kliknite "Dodaj zaposlenog" da biste uneli kontakte
                </p>
              </div>
            )}
            {fields.map((field, idx) => (
              <div
                key={field.id}
                className="p-4 rounded-xl border space-y-3"
                style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--muted) / 0.3)' }}
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold" style={{ color: 'hsl(var(--muted-foreground))' }}>
                    Zaposleni #{idx + 1}
                  </p>
                  <button
                    type="button"
                    className="btn-ghost p-1"
                    style={{ color: 'hsl(var(--destructive))' }}
                    onClick={() => remove(idx)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="label text-xs">Ime i prezime</label>
                    <input
                      className="input-field text-sm"
                      placeholder="Ime Prezime"
                      {...register(`employees.${idx}.name`, { required: 'Ime je obavezno' })}
                    />
                    <InputError msg={errors.employees?.[idx]?.name?.message} />
                  </div>
                  <div>
                    <label className="label text-xs">Pozicija</label>
                    <input
                      className="input-field text-sm"
                      placeholder="npr. Računovođa"
                      {...register(`employees.${idx}.position`)}
                    />
                  </div>
                  <div>
                    <label className="label text-xs">Telefon</label>
                    <input
                      className="input-field text-sm"
                      placeholder="+381 63 ..."
                      {...register(`employees.${idx}.phone`)}
                    />
                  </div>
                  <div>
                    <label className="label text-xs">E-mail</label>
                    <input
                      type="email"
                      className="input-field text-sm"
                      placeholder="ime@firma.rs"
                      {...register(`employees.${idx}.email`)}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Actions */}
        <div
          className="flex justify-end gap-3 pt-2 border-t sticky bottom-0 py-4 -mx-6 px-6"
          style={{ borderColor: 'hsl(var(--border))', backgroundColor: 'hsl(var(--card))' }}
        >
          <button type="button" className="btn-secondary" onClick={onClose}>
            Otkaži
          </button>
          <button type="submit" className="btn-primary min-w-[140px]" disabled={isSubmitting}>
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                Čuvanje...
              </span>
            ) : (initial ? 'Sačuvaj izmene' : 'Dodaj klijenta')}
          </button>
        </div>
      </form>
    </Modal>
  );
}